import { Outlet, createFileRoute, redirect, useRouterState } from '@tanstack/react-router'
import type { JSX } from 'react'

import { AuthPanel } from '@/components/auth/auth-panel'
import { VerificationHeader } from '@/components/layout/verification-header'
import { ThemeToggle } from '@/components/ui/theme-toggle'
import { refreshAccessToken } from '@/lib/api/client'
import { LOGO } from '@/lib/assets'
import { useAuthStore } from '@/lib/auth/store'
import { getVerificationState } from '@/lib/auth/verification'

const VERIFICATION_ROUTES = new Set(['/verify-email', '/verify-phone'])

export const Route = createFileRoute('/_public')({
  // beforeLoad es async para poder intentar el silent refresh antes de renderizar
  // cualquier ruta pública de auth (login, register, forgot-password, reset-password).
  // Así un usuario con sesión activa que escribe /login nunca ve el formulario.
  beforeLoad: async ({ location }) => {
    const isVerificationRoute = VERIFICATION_ROUTES.has(location.pathname)
    const { token, step } = getVerificationState()

    if (isVerificationRoute) {
      if (token) {
        // Redirigir al paso correcto si la URL no coincide con el step pendiente
        if (location.pathname === '/verify-email' && step === 'phone') {
          throw redirect({ to: '/verify-phone' })
        }
        if (location.pathname === '/verify-phone' && step === 'email') {
          throw redirect({ to: '/verify-email' })
        }
        return
      }
      // Sin token de verificación en una ruta de verificación: bloquear
      throw redirect({ to: '/login' })
    }

    // Rutas de auth (login, register, forgot-password, reset-password)

    // Si hay proceso de verificación en curso → completarlo primero
    if (token) {
      throw redirect({ to: step === 'phone' ? '/verify-phone' : '/verify-email' })
    }

    // Token en memoria → sesión activa garantizada → dashboard inmediato (sin red)
    if (useAuthStore.getState().accessToken) {
      throw redirect({ to: '/dashboard' })
    }

    // Sin token en memoria: puede existir sesión via cookie (después de recargar la página).
    // Intentamos silent refresh — si la cookie es válida el servidor devuelve un nuevo
    // access token y redirigimos al dashboard. Si falla (401 / sin cookie) continuamos
    // normalmente y mostramos el formulario de auth.
    let sessionRestored = false
    try {
      await refreshAccessToken()
      sessionRestored = true
    } catch {
      // 401 o error de red → no hay sesión activa → mostrar ruta pública
    }
    if (sessionRestored) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: PublicLayout,
})

function PublicLayout(): JSX.Element {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const isVerificationRoute = VERIFICATION_ROUTES.has(pathname)

  if (isVerificationRoute) {
    return (
      <div className="flex min-h-[100dvh] flex-col">
        <VerificationHeader />
        {/* Dos correcciones sobre el primer intento del rediseño:
            1. `max-w-3xl` (768px) era insuficiente para dos columnas reales —
               con la derecha fija a `23rem`, a la izquierda le quedaban
               ~360px: el titular envolvía en 3 líneas y el párrafo se leía
               apretado contra el resto. `max-w-5xl` (1024px) le da a cada
               columna el ancho que el contenido necesita.
            2. `items-center` en un contenedor de toda la altura del viewport
               centraba el bloque verticalmente — en una pantalla alta eso deja
               cientos de píxeles de vacío arriba Y abajo de un contenido corto.
               `items-start` con `pt-*` ancla el contenido cerca del header,
               como el resto de las pantallas de tarea del producto (ninguna
               otra vista de Abakoo centra su contenido en el alto completo). */}
        <div className="flex flex-1 items-start justify-center px-4 pt-10 pb-10 sm:px-6 sm:pt-16">
          <div className="w-full max-w-5xl">
            <Outlet />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-[100dvh]">
      {/* ── Panel izquierdo: tema inverso — ver auth-panel.tsx ── */}
      <AuthPanel />

      {/* ── Panel derecho: formulario ── */}
      <div className="relative flex flex-1 flex-col items-center justify-center px-4 py-10 sm:px-8 lg:px-16">
        {/* Barra superior derecha: toggle de tema */}
        <div className="absolute top-[max(1rem,env(safe-area-inset-top))] right-[max(1rem,env(safe-area-inset-right))]">
          <ThemeToggle />
        </div>

        {/* Logo de Abakoo: visible solo en mobile (el panel izquierdo está oculto) */}
        <div className="mb-8 lg:hidden">
          <img src={LOGO.light} alt="Abakoo" className="h-9 w-auto dark:hidden" />
          <img src={LOGO.dark} alt="Abakoo" className="hidden h-9 w-auto dark:block" />
        </div>

        <div className="w-full max-w-md">
          <Outlet />
        </div>
      </div>
    </div>
  )
}
